import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';

interface IApiEnvelope<T> {
  data: T;
  message?: string;
  meta?: unknown;
}

const BASE = '/api/v1/contacil/regras';

export interface IIaProgresso {
  empresa_id: number;
  total: number;
  conciliados: number;
  pendentes: number;
  ia_tentou: number; // a IA analisou (dt_ia_tentativa)
  ia_conciliou: number; // conciliações da IA que vingaram (fonte_match='ia')
  residuo: number; // IA tentou + pendente + sem contrapartida
  taxa_acerto: number | null; // ia_conciliou / (ia_conciliou + residuo)
}

/** KPIs de acerto da IA por empresa (School AI — aba Progresso). */
export const useContaCilIaProgresso = (empresaId?: number | null) =>
  useQuery({
    queryKey: ['contacil', 'school-ai', 'progresso', empresaId ?? null] as const,
    enabled: empresaId != null,
    queryFn: async (): Promise<IIaProgresso> => {
      const res = await api.get<IApiEnvelope<IIaProgresso>>(`${BASE}/ia/progresso`, {
        params: { empresa_id: empresaId },
      });
      return res.data.data;
    },
  });

export interface IResiduoLancamento {
  id: number;
  data: string;
  historico: string;
  dc: 'D' | 'C';
  valor: number;
  banco_nome?: string | null;
  categoria?: string | null;
}

export interface IResiduoMemoria {
  poc_codigo: string;
  pureza: number;
  status: string;
  conflito: boolean;
}

export interface IResiduoCluster {
  chave_norm: string;
  contraparte_exibe: string;
  cnpj_cpf?: string | null;
  dc: 'D' | 'C';
  qtd: number;
  soma: number;
  valor_min: number;
  valor_max: number;
  dispersao: 'baixa' | 'alta';
  sugere_regra: boolean;
  memoria?: IResiduoMemoria | null;
  itens: IResiduoLancamento[];
}

export interface IResiduoClustersResp {
  clusters: IResiduoCluster[];
  one_offs: IResiduoLancamento[];
  total_lancamentos: number;
  total_soma: number;
  n_clusters: number;
}

/** Resíduo agrupado por contraparte (alavancagem) — aba Resíduo do School AI. */
export const useContaCilResiduoClusters = (empresaId?: number | null) =>
  useQuery({
    queryKey: ['contacil', 'school-ai', 'clusters', empresaId ?? null] as const,
    enabled: empresaId != null,
    queryFn: async (): Promise<IResiduoClustersResp> => {
      const res = await api.get<IApiEnvelope<IResiduoClustersResp>>(
        '/api/v1/contacil/lancamentos/residuo-clusters',
        { params: { empresa_id: empresaId } },
      );
      return res.data.data;
    },
  });

// Sprint 1 — Dashboard home (4 cards + serie 30d).

export interface ICardHoje {
  processados: number;
  conciliados: number;
  pendentes: number;
  via_ia: number;
  via_regra: number;
  via_memoria: number;
  via_manual: number;
  custo_ia_usd: number;
  eventos_total: number;
}

export interface ICardAtencao {
  memorias_conflito: number;
  memorias_quarentena: number;
  regras_alto_revert: number;
  residuos_recentes_7d: number;
}

export interface ICardAprendizado {
  memorias_confiaveis: number;
  regras_ia_aceitas: number;
  correcoes_humanas_7d: number;
  taxa_acerto_atual: number | null;
}

export interface ISerieDia {
  data: string; // YYYY-MM-DD
  processados: number;
  conciliados: number;
  taxa: number | null;
}

export interface ISchoolAiDashboard {
  empresa_id: number;
  gerado_em: string;
  hoje: ICardHoje;
  atencao: ICardAtencao;
  aprendizado: ICardAprendizado;
  serie_30d: ISerieDia[];
}

/** Dashboard home agregado — 4 cards + sparkline 30d em 1 request. */
export const useContaCilSchoolAiDashboard = (empresaId?: number | null) =>
  useQuery({
    queryKey: ['contacil', 'school-ai', 'dashboard', empresaId ?? null] as const,
    enabled: empresaId != null,
    staleTime: 60 * 1000, // 1 min — KPIs nao precisam de tempo real
    queryFn: async (): Promise<ISchoolAiDashboard> => {
      const res = await api.get<IApiEnvelope<ISchoolAiDashboard>>(
        '/api/v1/contacil/school-ai/dashboard',
        { params: { empresa_id: empresaId } },
      );
      return res.data.data;
    },
  });

// Sprint 2 — Auditor passivo (drift + regras decaindo + anomalias).

export interface IAuditorDriftMemoria {
  memoria_id: number;
  contraparte_norm: string;
  dc: 'D' | 'C';
  poc_codigo: string | null;
  poc_classificacao: string | null;
  suporte: number;
  pureza: number;
  status: string;
  dt_atualizacao: string;
  origem: string;
}

export interface IAuditorRegraDecaindo {
  regra_id: number;
  categoria: string | null;
  kw: string[];
  poc_codigo: string | null;
  qtd_aplicada: number;
  qtd_revertida: number;
  revert_rate: number;
  criada_por_ia: boolean;
  validada: boolean;
}

export interface IAuditorAnomaliaCluster {
  contraparte_norm: string;
  contraparte_exibe: string;
  dc: 'D' | 'C';
  qtd_atual: number;
  mediana_30d: number;
  razao: number;
}

export interface ISchoolAiAuditor {
  empresa_id: number;
  gerado_em: string;
  drift_memoria: IAuditorDriftMemoria[];
  regras_decaindo: IAuditorRegraDecaindo[];
  anomalias_cluster: IAuditorAnomaliaCluster[];
}

/** Auditor passivo — drift de memoria, regras decaindo, anomalias. */
export const useContaCilSchoolAiAuditor = (empresaId?: number | null) =>
  useQuery({
    queryKey: ['contacil', 'school-ai', 'auditor', empresaId ?? null] as const,
    enabled: empresaId != null,
    staleTime: 5 * 60 * 1000, // 5 min — auditor nao precisa real-time
    queryFn: async (): Promise<ISchoolAiAuditor> => {
      const res = await api.get<IApiEnvelope<ISchoolAiAuditor>>(
        '/api/v1/contacil/school-ai/auditor',
        { params: { empresa_id: empresaId } },
      );
      return res.data.data;
    },
  });

// F4.5 gap #2 — Divergências regra × razão (lista de revisão pro contador).

export interface IDivergenciaRegra {
  regra_id: number;
  categoria: string;
  kw: string[];
  dc?: string | null;
  escopo: string; // 'empresa' | 'global'
  criada_por_ia: boolean;
  poc_regra: string; // conta que a regra atribui
  poc_razao_dominante?: string | null; // conta dominante no razão
  suporte_dominante: number;
  total_postings: number;
  pureza: number;
  veredito: 'CONFIRMADA' | 'DIVERGE' | 'SEM_DADOS';
}

export interface IDivergenciasResp {
  empresa_id: number;
  total_regras: number;
  divergem: number;
  confirmadas: number;
  sem_dados: number;
  itens: IDivergenciaRegra[];
}

/** Confronta as regras com o razão (o professor). DIVERGE = revisar a regra. */
export const useContaCilDivergenciasRazao = (empresaId?: number | null) =>
  useQuery({
    queryKey: ['contacil', 'school-ai', 'divergencias', empresaId ?? null] as const,
    enabled: empresaId != null,
    staleTime: 5 * 60 * 1000, // varre o razão — não precisa real-time
    queryFn: async (): Promise<IDivergenciasResp> => {
      const res = await api.get<IApiEnvelope<IDivergenciasResp>>(
        '/api/v1/contacil/school-ai/divergencias-razao',
        { params: { empresa_id: empresaId } },
      );
      return res.data.data;
    },
  });

// Sprint 2 — Promover memoria (quarentena → confiavel).

export const useContaCilPromoverMemoria = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (memoriaId: number): Promise<{ id: number; status: string }> => {
      const res = await api.patch<IApiEnvelope<{ id: number; status: string }>>(
        `/api/v1/contacil/regras/memoria/${memoriaId}/promover`,
      );
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacil', 'school-ai'] });
      qc.invalidateQueries({ queryKey: ['contacil', 'empresas-memoria'] });
    },
  });
};

// Sprint 2 — Criar regra a partir de cluster do residuo.

export interface ICriarRegraDoClusterPayload {
  empresa_id: number;
  contraparte_norm: string;
  categoria: string;
  dc: 'D' | 'C';
  kw?: string[];
  poc_codigo?: string | null;
  banco?: string | null;
  escopo_global?: boolean;
}

export interface IRegraCriadaResult {
  id: number;
  empresa_id: number | null;
  categoria: string;
  kw: string[];
  poc_codigo: string | null;
}

export const useContaCilCriarRegraDoCluster = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: ICriarRegraDoClusterPayload): Promise<IRegraCriadaResult> => {
      const res = await api.post<IApiEnvelope<IRegraCriadaResult>>(
        '/api/v1/contacil/regras/criar-do-cluster',
        payload,
      );
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacil', 'school-ai'] });
      qc.invalidateQueries({ queryKey: ['contacil', 'regras'] });
    },
  });
};

// (Reverter IA com motivo vive em useContaCilRegras.useContaCilIaReverter —
//  o duplicado que existia aqui nunca foi importado por nenhuma tela.)

export interface IConfirmarSugeridosResult {
  confirmados: number; // viraram conciliado + alimentaram a memória
  ignorados: number; // não eram SUGERE pré-preenchido (status/contrapartida)
  memorias_gravadas: number; // correções que viraram memória
}

/**
 * Fila SUGERE (F4.5): confirma em lote os pré-preenchidos (1-clique/lote).
 * Cada confirmação vira conciliado e alimenta a memória L2 — o multiplicador
 * humano da refinaria. Invalida a lista de lançamentos + os painéis School AI.
 */
export const useContaCilConfirmarSugeridos = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      empresa_id: number;
      ids: number[];
    }): Promise<IConfirmarSugeridosResult> => {
      const res = await api.post<IApiEnvelope<IConfirmarSugeridosResult>>(
        '/api/v1/contacil/lancamentos/confirmar-sugeridos',
        payload,
      );
      return res.data.data;
    },
    onSuccess: () => {
      // Mesmo conjunto do useInvalidarLancamentos (a confirmação mexe nas
      // contagens de pendente/conciliado) + os painéis School AI.
      qc.invalidateQueries({ queryKey: ['contacil', 'lancamentos'] });
      qc.invalidateQueries({ queryKey: ['contacil', 'lancamentos-resumo'] });
      qc.invalidateQueries({ queryKey: ['contacil', 'empresas-saude'] });
      qc.invalidateQueries({ queryKey: ['contacil', 'school-ai'] });
    },
  });
};

// Sprint 3 — Golden Set (prova de resistencia da IA).

export interface IGoldenItem {
  id: number;
  lancamento_id: number;
  historico_snapshot: string;
  dc: 'D' | 'C';
  valor: number;
  contraparte: string | null;
  contraparte_cnpj_cpf: string | null;
  banco_nome: string | null;
  categoria_label: string | null;
  debito_poc_label: string | null;
  credito_poc_label: string | null;
  motivo: string | null;
  ativo: boolean;
  dt_criacao: string;
}

export interface IGoldenSetList {
  itens: IGoldenItem[];
  total: number;
  ativos: number;
}

export interface IGoldenRunItemDetalhe {
  golden_id: number;
  lancamento_id: number;
  historico: string;
  esperado_poc: string | null;
  predito_poc: string | null;
  acertou_poc: boolean;
  esperado_categoria: string | null;
  predito_categoria: string | null;
  acertou_categoria: boolean;
  score: number | null;
  justificativa: string | null;
}

export interface IGoldenRunResult {
  id: number;
  empresa_id: number;
  dt_criacao: string;
  qtd_total: number;
  qtd_acerto_poc: number;
  qtd_acerto_categoria: number;
  qtd_erro: number;
  precision_poc: number;
  recall_poc: number;
  f1_poc: number;
  modelo: string | null;
  prompt_version: string | null;
  detalhe: IGoldenRunItemDetalhe[];
}

export interface IGoldenRunHistoricoItem {
  id: number;
  dt_criacao: string;
  qtd_total: number;
  qtd_acerto_poc: number;
  qtd_acerto_categoria: number;
  f1_poc: number;
  modelo: string | null;
  prompt_version: string | null;
}

export const useContaCilGoldenSet = (empresaId?: number | null) =>
  useQuery({
    queryKey: ['contacil', 'school-ai', 'golden', empresaId ?? null] as const,
    enabled: empresaId != null,
    queryFn: async (): Promise<IGoldenSetList> => {
      const res = await api.get<IApiEnvelope<IGoldenSetList>>('/api/v1/contacil/school-ai/golden', {
        params: { empresa_id: empresaId },
      });
      return res.data.data;
    },
  });

export const useContaCilGoldenHistorico = (empresaId?: number | null) =>
  useQuery({
    queryKey: ['contacil', 'school-ai', 'golden-historico', empresaId ?? null] as const,
    enabled: empresaId != null,
    queryFn: async (): Promise<{ runs: IGoldenRunHistoricoItem[] }> => {
      const res = await api.get<IApiEnvelope<{ runs: IGoldenRunHistoricoItem[] }>>(
        '/api/v1/contacil/school-ai/golden/historico',
        { params: { empresa_id: empresaId } },
      );
      return res.data.data;
    },
  });

export const useContaCilGoldenCurar = (empresaId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      lancamento_id: number;
      motivo?: string;
    }): Promise<IGoldenItem> => {
      const res = await api.post<IApiEnvelope<IGoldenItem>>(
        '/api/v1/contacil/school-ai/golden/curar',
        { lancamento_id: params.lancamento_id, motivo: params.motivo },
        { params: { empresa_id: empresaId } },
      );
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacil', 'school-ai', 'golden'] });
    },
  });
};

export const useContaCilGoldenRemover = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (goldenId: number) => {
      const res = await api.delete<IApiEnvelope<{ id: number }>>(
        `/api/v1/contacil/school-ai/golden/${goldenId}`,
      );
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacil', 'school-ai', 'golden'] });
    },
  });
};

export const useContaCilGoldenRun = (empresaId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<IGoldenRunResult> => {
      const res = await api.post<IApiEnvelope<IGoldenRunResult>>(
        '/api/v1/contacil/school-ai/golden/run',
        {},
        { params: { empresa_id: empresaId } },
      );
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacil', 'school-ai', 'golden-historico'] });
      qc.invalidateQueries({ queryKey: ['contacil', 'school-ai', 'dashboard'] });
    },
  });
};

// Prova do motor determinístico (0 tokens): mesma bateria/comparador do
// golden/run, com a cascata regras+memória+razão no lugar da IA. O run
// entra no mesmo histórico com modelo='motor-vX' (A/B IA vs motor).
export const useContaCilGoldenRunMotor = (empresaId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<IGoldenRunResult> => {
      const res = await api.post<IApiEnvelope<IGoldenRunResult>>(
        '/api/v1/contacil/school-ai/golden/run-motor',
        {},
        { params: { empresa_id: empresaId } },
      );
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacil', 'school-ai', 'golden-historico'] });
      qc.invalidateQueries({ queryKey: ['contacil', 'school-ai', 'dashboard'] });
    },
  });
};

// Sprint 4 — Autopromocao (Onda 2).

export interface IAutopromoverStats {
  empresa_id: number;
  dt_execucao: string;
  regras_processadas: number;
  regras_validadas: number;
  memorias_promovidas: number;
  lancamentos_liberados: number;
  cutoff_dias: number;
  regras_criadas_auto: number;
  candidatos_avaliados: number;
}

export const useContaCilAutopromover = (empresaId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<IAutopromoverStats> => {
      const res = await api.post<IApiEnvelope<IAutopromoverStats>>(
        '/api/v1/contacil/school-ai/job/autopromover',
        {},
        { params: { empresa_id: empresaId } },
      );
      return res.data.data;
    },
    onSuccess: () => {
      // Invalida tudo do school-ai porque o job mexe em tudo.
      qc.invalidateQueries({ queryKey: ['contacil', 'school-ai'] });
      qc.invalidateQueries({ queryKey: ['contacil', 'regras'] });
      qc.invalidateQueries({ queryKey: ['contacil', 'empresas-memoria'] });
    },
  });
};

// Sprint 5 — Timeline + Debug + Comparacao A/B

export interface ILancamentoEvento {
  id: number;
  dt_evento: string;
  evento_tipo: string;
  fonte: string | null;
  usuario_id: number | null;
  motivo: string | null;
  valor_antes: Record<string, unknown> | null;
  valor_depois: Record<string, unknown> | null;
  campos_alterados: string[] | null;
  meta_extra: Record<string, unknown> | null;
}

export interface ILancamentoTimeline {
  lancamento_id: number;
  eventos: ILancamentoEvento[];
}

export const useContaCilLancamentoTimeline = (lancamentoId?: number | null) =>
  useQuery({
    queryKey: ['contacil', 'lancamento', 'timeline', lancamentoId ?? null] as const,
    enabled: lancamentoId != null,
    queryFn: async (): Promise<ILancamentoTimeline> => {
      const res = await api.get<IApiEnvelope<ILancamentoTimeline>>(
        `/api/v1/contacil/lancamentos/${lancamentoId}/timeline`,
      );
      return res.data.data;
    },
  });

export interface IDebugPasso {
  fonte: string;
  status: string;
  detalhe: string;
  referencia_id: number | null;
}

export interface ILancamentoDebug {
  lancamento_id: number;
  status_atual: string;
  fonte_match: string | null;
  perna_faltando: string | null;
  passos: IDebugPasso[];
  sugestoes_ia_top3: Array<{ cod?: string; nm?: string; score?: number }> | null;
}

export const useContaCilLancamentoDebug = (lancamentoId?: number | null) =>
  useQuery({
    queryKey: ['contacil', 'lancamento', 'debug', lancamentoId ?? null] as const,
    enabled: lancamentoId != null,
    queryFn: async (): Promise<ILancamentoDebug> => {
      const res = await api.get<IApiEnvelope<ILancamentoDebug>>(
        `/api/v1/contacil/lancamentos/${lancamentoId}/debug`,
      );
      return res.data.data;
    },
  });

export interface IGoldenComparacaoItem {
  golden_id: number;
  historico: string;
  esperado_poc: string | null;
  predito_a: string | null;
  predito_b: string | null;
  acertou_a: boolean;
  acertou_b: boolean;
  tipo: 'regressao' | 'melhoria' | 'estavel_ok' | 'estavel_erro' | 'mudou_resposta';
}

export interface IGoldenComparacao {
  run_a_id: number;
  run_b_id: number;
  run_a_f1: number;
  run_b_f1: number;
  delta_f1: number;
  qtd_regressoes: number;
  qtd_melhorias: number;
  qtd_estavel_ok: number;
  qtd_estavel_erro: number;
  qtd_mudou_resposta: number;
  itens: IGoldenComparacaoItem[];
}

export const useContaCilGoldenComparar = (
  empresaId?: number | null,
  runA?: number | null,
  runB?: number | null,
) =>
  useQuery({
    queryKey: ['contacil', 'school-ai', 'golden-comparar', empresaId, runA, runB] as const,
    enabled: empresaId != null && runA != null && runB != null && runA !== runB,
    queryFn: async (): Promise<IGoldenComparacao> => {
      const res = await api.get<IApiEnvelope<IGoldenComparacao>>(
        '/api/v1/contacil/school-ai/golden/comparar',
        { params: { empresa_id: empresaId, run_a: runA, run_b: runB } },
      );
      return res.data.data;
    },
  });

// Sprint 7 — RAG debug (mostra exemplos do livro-razao no modal).

export interface IRagExemplo {
  id: number;
  historico: string;
  conta_debito: string | null;
  conta_debito_nome: string | null;
  conta_credito: string | null;
  conta_credito_nome: string | null;
  valor: number | null;
  data: string | null;
  score: number;
  origem_humano: boolean;
  fonte_match_cross: string | null;
}

export interface IRagDebug {
  lancamento_id: number;
  historico_query?: string;
  qtd: number;
  exemplos: IRagExemplo[];
  aviso?: string;
}

export const useContaCilRagDebug = (lancamentoId?: number | null) =>
  useQuery({
    queryKey: ['contacil', 'school-ai', 'rag-debug', lancamentoId ?? null] as const,
    enabled: lancamentoId != null,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<IRagDebug> => {
      const res = await api.get<IApiEnvelope<IRagDebug>>('/api/v1/contacil/school-ai/rag-debug', {
        params: { lancamento_id: lancamentoId, k: 5 },
      });
      return res.data.data;
    },
  });

// Sprint 8 — Embeddings semanticos (Voyage AI).

export interface IEmbeddingsStatus {
  empresa_id: number;
  qtd_total: number;
  qtd_indexado: number;
  qtd_indexado_modelo_atual: number;
  modelo_atual: string | null;
  dim?: number;
  aviso?: string;
}

export const useContaCilEmbeddingsStatus = (empresaId?: number | null) =>
  useQuery({
    queryKey: ['contacil', 'school-ai', 'embeddings-status', empresaId ?? null] as const,
    enabled: empresaId != null,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<IEmbeddingsStatus> => {
      const res = await api.get<IApiEnvelope<IEmbeddingsStatus>>(
        '/api/v1/contacil/school-ai/embeddings/status',
        { params: { empresa_id: empresaId } },
      );
      return res.data.data;
    },
  });

export interface IEmbeddingsReembedResult {
  qtd_processada: number;
  qtd_pulada_hash: number;
  modelo: string;
  mensagem: string;
}

export const useContaCilEmbeddingsReembed = (empresaId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params?: {
      limite?: number;
      forcar?: boolean;
    }): Promise<IEmbeddingsReembedResult> => {
      const res = await api.post<IApiEnvelope<IEmbeddingsReembedResult>>(
        '/api/v1/contacil/school-ai/embeddings/reembed',
        {},
        {
          params: {
            empresa_id: empresaId,
            limite: params?.limite ?? 1000,
            forcar: params?.forcar ?? false,
          },
          timeout: 120000,
        },
      );
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacil', 'school-ai', 'embeddings-status'] });
      qc.invalidateQueries({ queryKey: ['contacil', 'school-ai', 'rag-debug'] });
    },
  });
};

// Sprint 9 — Memoria global (cross-empresa).

export interface IMemoriaGlobalTop {
  contraparte_norm: string;
  dc: 'D' | 'C';
  poc_codigo_sugerido: string | null;
  poc_classificacao_sugerida: string | null;
  n_empresas: number;
  pureza: number;
}

export interface IMemoriaGlobalStatus {
  total: number;
  qualificados_pra_ia: number;
  ultimo_recalc: string | null;
  top: IMemoriaGlobalTop[];
}

export const useContaCilMemoriaGlobalStatus = () =>
  useQuery({
    queryKey: ['contacil', 'school-ai', 'memoria-global-status'] as const,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<IMemoriaGlobalStatus> => {
      const res = await api.get<IApiEnvelope<IMemoriaGlobalStatus>>(
        '/api/v1/contacil/school-ai/memoria-global/status',
        { params: { top: 10 } },
      );
      return res.data.data;
    },
  });

export const useContaCilMemoriaGlobalAgregar = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<{ padroes_globais_total: number }> => {
      const res = await api.post<IApiEnvelope<{ padroes_globais_total: number }>>(
        '/api/v1/contacil/school-ai/memoria-global/agregar',
      );
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacil', 'school-ai', 'memoria-global-status'] });
    },
  });
};
